import React, { useState, useEffect, useCallback } from 'react';
import { wayleaveDocsAPI, sharepointAPI } from '../utils/api';
import { formatDate } from '../lib/date';
import SharePointFolderPicker from './SharePointFolderPicker';

// Setup tab: manage the shared library of appointment letters per task order.
// A letter is uploaded once against a task order, then linked from any pack on
// that task order when a new pack is created (spec §3).
function WayleaveDocsSetup() {
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Add form
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ taskOrderRef: '', title: '', issuedBy: '', receivedDate: '' });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // SharePoint connection state
  const [spConn, setSpConn] = useState(null);
  const [spLoading, setSpLoading] = useState(true);
  const [siteUrl, setSiteUrl] = useState('');
  const [resolved, setResolved] = useState(null); // { site, drives }
  const [driveChoice, setDriveChoice] = useState('');
  const [spMsg, setSpMsg] = useState('');
  const [spErr, setSpErr] = useState('');
  const [testing, setTesting] = useState(false);
  const [showDrePicker, setShowDrePicker] = useState(false);

  const loadSpConn = useCallback(async () => {
    setSpLoading(true);
    try {
      const res = await sharepointAPI.getConnection();
      setSpConn(res.data.connection || null);
      if (res.data.connection?.site_url) setSiteUrl(res.data.connection.site_url);
    } catch { setSpConn(null); }
    finally { setSpLoading(false); }
  }, []);
  useEffect(() => { loadSpConn(); }, [loadSpConn]);

  const resolveSite = async () => {
    setSpErr(''); setSpMsg(''); setResolved(null);
    try {
      const res = await sharepointAPI.resolveSite(siteUrl.trim());
      setResolved(res.data);
      setDriveChoice(res.data.drives?.[0]?.id || '');
    } catch (err) { setSpErr(err.response?.data?.error || 'Failed to resolve site'); }
  };
  const saveConn = async () => {
    setSpErr(''); setSpMsg('');
    const drive = resolved?.drives?.find((d) => d.id === driveChoice);
    try {
      await sharepointAPI.saveConnection({ siteUrl: siteUrl.trim(), siteId: resolved.site.id, driveId: drive.id, driveName: drive.name });
      setSpMsg('Connection saved.'); setResolved(null); await loadSpConn();
    } catch (err) { setSpErr(err.response?.data?.error || 'Failed to save connection'); }
  };
  const testConn = async () => {
    setTesting(true); setSpErr(''); setSpMsg('');
    try { await sharepointAPI.testConnection(); setSpMsg('Connection OK — the app can read/write this site.'); }
    catch (err) { setSpErr(err.response?.data?.error || 'Connection test failed'); }
    finally { setTesting(false); }
  };
  const chooseDreFolder = async (folder) => {
    setShowDrePicker(false); setSpErr(''); setSpMsg('');
    try { await sharepointAPI.setDreFolder(folder.id, folder.path); setSpMsg('DRE folder set.'); await loadSpConn(); }
    catch (err) { setSpErr(err.response?.data?.error || 'Failed to set DRE folder'); }
  };

  const fetchLetters = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await wayleaveDocsAPI.getAppointments();
      setLetters(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load appointment letters');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLetters(); }, [fetchLetters]);

  const resetForm = () => {
    setForm({ taskOrderRef: '', title: '', issuedBy: '', receivedDate: '' });
    setFile(null);
    setFormError('');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.taskOrderRef.trim()) { setFormError('Task order name is required'); return; }
    if (!file) { setFormError('Please attach the appointment letter file'); return; }
    setSaving(true);
    setFormError('');
    try {
      const fd = new FormData();
      fd.append('taskOrderRef', form.taskOrderRef.trim());
      if (form.title.trim()) fd.append('title', form.title.trim());
      if (form.issuedBy) fd.append('issuedBy', form.issuedBy);
      if (form.receivedDate) fd.append('receivedDate', form.receivedDate);
      fd.append('file', file);
      await wayleaveDocsAPI.createAppointment(fd);
      setShowAdd(false);
      resetForm();
      await fetchLetters();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save appointment letter');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (letter) => {
    if (letter.linkedCount > 0) return; // guarded server-side too
    if (!window.confirm(`Delete appointment letter "${letter.task_order_ref}"?`)) return;
    try {
      await wayleaveDocsAPI.deleteAppointment(letter.id);
      await fetchLetters();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete letter');
    }
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Setup — Appointment Letters</h2>
          <p className="text-sm text-gray-600">
            Create a task order and attach its appointment letter. These are offered when creating a new document pack.
          </p>
        </div>
        <button
          onClick={() => { setShowAdd(true); resetForm(); }}
          className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-1.5"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add appointment letter
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-3">{error}</div>}

      {/* Letters table */}
      <div className="card">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading…</div>
        ) : letters.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-sm text-gray-500">No appointment letters yet.</p>
            <button onClick={() => { setShowAdd(true); resetForm(); }} className="mt-3 text-sm text-primary-600 hover:text-primary-800 font-medium">
              Add the first one
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task order</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Issued by</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Received</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Linked packs</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Letter</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {letters.map((l) => (
                  <tr key={l.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{l.task_order_ref}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{l.title || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{l.issued_by || '—'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {formatDate(l.received_date)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{l.linkedCount}</td>
                    <td className="px-4 py-3 text-sm">
                      {l.fileUrl ? (
                        <a href={l.fileUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-800 inline-flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View
                        </a>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => remove(l)}
                        disabled={l.linkedCount > 0}
                        className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        title={l.linkedCount > 0 ? 'Linked to packs — cannot delete' : 'Delete letter'}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* SharePoint connection */}
      <div className="card mt-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">SharePoint connection</h2>
        <p className="text-sm text-gray-600 mb-4">Connect the archive SharePoint site. Documents are copied into each station&apos;s folder from the pack screen.</p>

        {spLoading ? (
          <div className="text-sm text-gray-500">Loading&hellip;</div>
        ) : (
          <>
            {spConn?.site_id ? (
              <div className="rounded-lg border border-gray-200 p-3 mb-3 text-sm">
                <p className="text-gray-800">Connected: <span className="font-medium">{spConn.site_url}</span></p>
                <p className="text-gray-500 text-xs mt-0.5">Library: {spConn.drive_name || '—'}</p>
                <p className="text-gray-500 text-xs mt-0.5">DRE folder: {spConn.dre_folder_path || <span className="text-amber-600">not set</span>}</p>
                <div className="flex gap-2 mt-2">
                  <button onClick={testConn} disabled={testing} className="text-xs font-medium text-primary-600 hover:text-primary-800 disabled:opacity-50">{testing ? 'Testing…' : 'Test connection'}</button>
                  <button onClick={() => setShowDrePicker(true)} className="text-xs font-medium text-primary-600 hover:text-primary-800">Set DRE / Site Handover folder</button>
                </div>
              </div>
            ) : null}

            <div className="flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[260px]">
                <label className="block text-xs font-medium text-gray-700 mb-1">Site URL</label>
                <input value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} placeholder="https://yourorg.sharepoint.com/sites/TCS-Projects"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
              </div>
              <button onClick={resolveSite} disabled={!siteUrl.trim()} className="px-3 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">Look up</button>
            </div>

            {resolved && (
              <div className="mt-3 flex flex-wrap items-end gap-2">
                <div className="min-w-[220px]">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Document library</label>
                  <select value={driveChoice} onChange={(e) => setDriveChoice(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg">
                    {resolved.drives.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <button onClick={saveConn} disabled={!driveChoice} className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">Save connection</button>
              </div>
            )}

            {spMsg && <p className="text-xs text-emerald-600 mt-2">{spMsg}</p>}
            {spErr && <p className="text-xs text-red-600 mt-2">{spErr}</p>}
          </>
        )}
      </div>

      {showDrePicker && (
        <SharePointFolderPicker
          title="Select the DRE / Site Handover folder"
          onSelect={chooseDreFolder}
          onClose={() => setShowDrePicker(false)}
        />
      )}

      {/* Add letter modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4" onClick={() => !saving && setShowAdd(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Add appointment letter</h3>
            <p className="text-sm text-gray-500 mb-4">Name the task order and attach the letter (PDF, JPG or PNG, max 25 MB).</p>

            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Task order name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.taskOrderRef}
                  onChange={(e) => setForm({ ...form, taskOrderRef: e.target.value })}
                  placeholder="e.g. TO-2026-14"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Title <span className="text-gray-400">(optional)</span></label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. N2 Section 1 wayleave appointment"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Issued by <span className="text-gray-400">(optional)</span></label>
                  <select
                    value={form.issuedBy}
                    onChange={(e) => setForm({ ...form, issuedBy: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">—</option>
                    <option value="WCG">WCG</option>
                    <option value="SANRAL">SANRAL</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Received date <span className="text-gray-400">(optional)</span></label>
                  <input
                    type="date"
                    value={form.receivedDate}
                    onChange={(e) => setForm({ ...form, receivedDate: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Appointment letter file <span className="text-red-500">*</span></label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                />
                {file && <p className="text-xs text-gray-500 mt-1">{file.name}</p>}
              </div>

              {formError && <p className="text-xs text-red-600">{formError}</p>}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAdd(false)}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? 'Uploading…' : 'Save letter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default WayleaveDocsSetup;
